import React, { useState, useEffect } from "react";
import UserCommon from "../../../components/frontend/UserCommon";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FrontendLayout from "../../../components/frontend/FrontendLayout";
import { handleApiError } from "../../../utils/apiHelpers";
import UserTabs from "../../../components/frontend/UserTabs";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface Errors {
  [key: string]: string;
}

interface ProfileData {
  name: string;
  email: string;
  imageUrl: string;
  userType: number;
}

const ChangePassword: React.FC = () => {
  const [formData, setFormData] = useState<ChangePasswordData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [profile, setProfile] = useState<ProfileData>({
    name: "",
    email: "",
    imageUrl: "frontend/images/user.jpg",
    userType: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("frontendUserToken");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/authuser/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.data.success) {
          const p = res.data.profile;
          setProfile({
            name: p.us_name || "",
            email: p.us_email || "",
            imageUrl: p.us_profileimage
              ? `${API_BASE}/uploads/profiles/${p.us_profileimage}`
              : "frontend/images/user.jpg",
            userType: p.us_usertype || 0,
          });
        } else {
          toast.error(res.data.message || "Failed to load user info.");
        }
      } catch (err: any) {
        const message = handleApiError(err, navigate);
        toast.error(message);
      }
    };

    fetchProfile();
  }, [token, navigate]);

  const validate = () => {
    const newErrors: Errors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required.";
    }
    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required.";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "New password must be at least 8 characters.";
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password.";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const res = await axios.post(
        `${API_BASE}/api/authuser/change-password`,
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.success) {
        toast.dismiss();
        toast.success(res.data.message || "Password changed successfully.");
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        toast.dismiss();
        toast.error(res.data.message || "Failed to change password.");
      }
    } catch (err: any) {
      const message = handleApiError(err, navigate);
      toast.error(message);
    } finally {
      setIsSubmitting(false); // Stop loader
    }
  };

  return (
    <FrontendLayout>
      <ToastContainer />
      <div className="user">
        <div className="tf-container py-5 user">
          {/* ✅ UserCommon component */}
          <UserCommon
            name={profile.name}
            email={profile.email}
            imageUrl={profile.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <h5 className="mb-4 title text-center">Change Password</h5>
              <hr />

              <form onSubmit={handleSubmit}>
                <fieldset className="box grid-layout-3 gap-30 form">
                  <div className="box-fieldset">
                    <label htmlFor="currentPassword">
                      Current Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.currentPassword && (
                      <div className="text-danger small mt-1">
                        {errors.currentPassword}
                      </div>
                    )}
                  </div>

                  <div className="box-fieldset">
                    <label htmlFor="newPassword">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.newPassword && (
                      <div className="text-danger small mt-1">
                        {errors.newPassword}
                      </div>
                    )}
                  </div>
                  <div className="box-fieldset">
                    <label htmlFor="confirmPassword">
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.confirmPassword && (
                      <div className="text-danger small mt-1">
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>
                </fieldset>

                <div className="box pt-5">
                  <button
                    type="submit"
                    className="tf-btn bg-color-primary pd-20"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </FrontendLayout>
  );
};

export default ChangePassword;
